import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Barbell, Fire, MoonStars, Heart, ArrowRight, Brain, Plus, Target, ForkKnife, BookOpen } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { canonicalRole } from "@/lib/roles";
import OnboardingWizard from "@/components/OnboardingWizard";
import DailyCheckIn from "@/components/DailyCheckIn";
import {
  AreaChart, Area, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";

const StatCard = ({ icon: Icon, label, value, unit, accent, testid }) => (
  <div className="tactical-card p-6" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <Icon size={20} weight="duotone" className={accent} />
      <span className="label-tiny">{label}</span>
    </div>
    <div className="flex items-baseline gap-2">
      <span className="font-heading text-4xl tabular-nums">{value}</span>
      {unit && <span className="label-tiny">{unit}</span>}
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const role = canonicalRole(user?.primary_role);
  const isAthlete = role === "athlete";
  const [stats, setStats] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    api.get("/stats/dashboard").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Onboarding wizard is a self-programming flow — only relevant for solo Individuals.
    // Athletes have a coach who programs for them. Coaches/admins/super admins don't need it.
    if (user && !user.onboarded_at && role === "general_client") {
      const t = setTimeout(() => setShowOnboarding(true), 400);
      return () => clearTimeout(t);
    }
  }, [user, role]);

  const hasAnyData = stats && (stats.total_workouts_7d > 0 || stats.total_calories_7d > 0 || stats.recovery_score > 0);
  const isNewUser = stats && !hasAnyData;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="dashboard-page">
      <OnboardingWizard open={showOnboarding} onComplete={() => setShowOnboarding(false)} />
      <DailyCheckIn />

      <div className="mb-10">
        <div className="label-tiny text-brand-accent mb-2 flex items-center gap-2">
          <span className="energy-dot"></span> // COMMAND CENTER
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl">
          WELCOME BACK, <span className="text-heat">{user?.name?.split(" ")[0]?.toUpperCase()}.</span>
        </h1>
        <p className="text-text-secondary mt-2 text-sm">7-day operational summary. All systems tracked.</p>
      </div>

      {/* New user hero panel */}
      {isNewUser && !showOnboarding && (
        <div className="tactical-card p-8 mb-8 stripe-bg relative overflow-hidden animate-fade-up" data-testid="new-user-hero">
          <div className="relative z-10 grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="label-tiny text-brand-accent mb-2 flex items-center gap-2">
                <Plus size={12} weight="fill" /> KICK-OFF
              </div>
              <h2 className="font-heading text-3xl mb-2">LET&apos;S LAY DOWN<br /><span className="text-heat">THE FIRST REP.</span></h2>
              <p className="text-sm text-text-secondary mb-6">Log your first workout, set a goal, or have the AI build your custom program.</p>
              <div className="flex flex-wrap gap-2">
                <Link to="/workouts" data-testid="hero-cta-log-workout">
                  <button className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest flex items-center gap-1.5">
                    <Barbell size={12} weight="bold" /> LOG WORKOUT
                  </button>
                </Link>
                {!isAthlete && (
                  <Link to="/ai-coach" data-testid="hero-cta-ai-program">
                    <button className="border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-surface-base px-4 py-2 text-xs label-tiny tracking-widest transition-colors flex items-center gap-1.5">
                      <Brain size={12} weight="bold" /> AI PROGRAM
                    </button>
                  </Link>
                )}
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="border border-white/10 hover:border-white/40 px-4 py-2 text-xs label-tiny tracking-widest transition-colors"
                  data-testid="hero-cta-onboarding"
                >
                  REDO ONBOARDING
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/10">
              {[
                { icon: Barbell, label: "WORKOUTS", val: "0", to: "/workouts" },
                { icon: Target, label: "GOALS", val: "0", to: "/goals" },
                { icon: ForkKnife, label: "MEALS", val: "0", to: "/nutrition" },
                { icon: MoonStars, label: "RECOVERY", val: "—", to: "/recovery" },
              ].map((s) => (
                <Link key={s.label} to={s.to} className="bg-surface-DEFAULT p-4 hover:bg-surface-elevated transition-colors">
                  <s.icon size={16} weight="duotone" className="text-brand-primary mb-2" />
                  <div className="font-heading text-xl tabular-nums">{s.val}</div>
                  <div className="label-tiny text-text-secondary">{s.label}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="stat-workouts" icon={Barbell} label="WORKOUTS / 7D" value={stats?.total_workouts_7d ?? "—"} unit="sessions" accent="text-brand-accent" />
        <StatCard testid="stat-duration" icon={Fire} label="ACTIVE TIME" value={stats?.total_duration_7d ?? "—"} unit="min" accent="text-brand-primary" />
        <StatCard testid="stat-calories" icon={Fire} label="CALORIES / 7D" value={stats?.total_calories_7d?.toLocaleString() ?? "—"} unit="kcal" accent="text-orange-400" />
        <StatCard testid="stat-recovery" icon={Heart} label="RECOVERY" value={stats?.recovery_score ?? "—"} unit="/ 100" accent="text-green-400" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="tactical-card p-6" data-testid="chart-workouts-week">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg">TRAINING VOLUME</h3>
            <span className="label-tiny">LAST 7 DAYS</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={stats?.chart || []}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4169E1" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#4169E1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" stroke="#A1A5B5" fontSize={11} />
              <YAxis stroke="#A1A5B5" fontSize={11} />
              <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
              <Area type="monotone" dataKey="duration" stroke="#4169E1" fill="url(#g1)" strokeWidth={2} dot={{ r: 3, fill: "#FFEA00" }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="tactical-card p-6" data-testid="chart-calories-week">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg">CALORIE INTAKE</h3>
            <span className="label-tiny">LAST 7 DAYS</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats?.chart || []}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" stroke="#A1A5B5" fontSize={11} />
              <YAxis stroke="#A1A5B5" fontSize={11} />
              <Tooltip contentStyle={{ background: "#12141A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} cursor={{ fill: "rgba(255,234,0,0.06)" }} />
              <Bar dataKey="calories" fill="#FFEA00" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { to: "/workouts", label: "LOG WORKOUT", desc: "Track today's session", testid: "quick-log-workout" },
          isAthlete
            ? { to: "/programs", label: "MY PROGRAMS", desc: "View coach-assigned plans", icon: BookOpen, testid: "quick-my-programs" }
            : { to: "/ai-coach", label: "BUILD AI PROGRAM", desc: "Generate a custom plan", icon: Brain, testid: "quick-ai-program" },
          { to: "/recovery", label: "LOG RECOVERY", desc: "Sleep & soreness", icon: MoonStars, testid: "quick-log-recovery" },
        ].map((q) => (
          <Link to={q.to} key={q.to} data-testid={q.testid}>
            <div className="tactical-card p-6 hover:bg-surface-elevated transition-colors group cursor-pointer">
              <div className="flex items-center justify-between mb-3">
                <span className="label-tiny text-brand-accent">// QUICK ACTION</span>
                <ArrowRight size={16} className="text-text-secondary group-hover:text-brand-accent group-hover:translate-x-1 transition-all" />
              </div>
              <div className="font-heading text-xl mb-1">{q.label}</div>
              <div className="text-xs text-text-secondary">{q.desc}</div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-8 text-center">
        <div className="label-tiny text-text-secondary">AVG SLEEP / 7D: <span className="text-white tabular-nums">{stats?.avg_sleep_7d ?? "—"}H</span></div>
      </div>
    </div>
  );
}
